import { SerializedConstructor } from '@langchain/core/load/serializable';
import { SyntaxNode, TreeCursor } from '@lezer/common';
import { parser as javascriptParser } from '@lezer/javascript';
import { parser as pythonParser } from '@lezer/python';

import { z } from 'zod';

import { fetcher } from '@/data/fetcher';
import {
  CommitManifestResponse,
  ExampleSchema,
  MessageTuple,
  RunSchema,
  StructuredEvaluatorSchema,
} from '@/types/schema';
import { hubApiCommitsPath } from '@/utils/constants';

import { SerializedChatPromptTemplateMessagesKwarg } from '../../Pages/Playground/components/manifest/serialized/prompts/ManifestChatPromptTemplate.utils';

const StructuredPromptJsonSchema = z.object({
  type: z.literal('object'),
  properties: z.record(z.unknown()),
});

const StructuredPromptManifestSchema = z.object({
  id: z.tuple([
    z.literal('langchain_core'),
    z.literal('prompts'),
    z.literal('structured'),
    z.literal('StructuredPrompt'),
  ]),
  kwargs: z.object({
    input_variables: z.array(z.string()),
    schema_: StructuredPromptJsonSchema,
  }),
});

export function parseOwnerRepoCommit(
  identifier: string
): [string, string, string | null] {
  let owner_repo: string = identifier;
  let commit: string | null = null;

  if (identifier.includes(':')) {
    [owner_repo, commit] = identifier.split(':');
  }

  if (!owner_repo.includes('/')) {
    return ['-', owner_repo, commit];
  }

  const [owner, repo] = owner_repo.split('/');
  return [owner, repo, commit];
}

export function convertSerializedMessagesToMessageTuple(
  messages: SerializedChatPromptTemplateMessagesKwarg
): [string, string][] {
  return messages.map((message) => {
    if ('prompt' in message.kwargs) {
      const role = message.id[3].split('Message')[0].toLowerCase();
      return [role, message.kwargs.prompt?.kwargs?.template ?? ''];
    } else {
      // 'prompt' property doesn't exist
      throw new Error('Not a valid structured prompt.');
    }
  });
}

function getFeedbackKeySansComment(
  keys: string[],
  properties: Record<string, unknown>
) {
  let keysSansComment = keys;
  if (
    [2, 3].includes(keysSansComment.length) &&
    keysSansComment.includes('comment')
  ) {
    keysSansComment = keysSansComment.filter((key) => key !== 'comment');
  }
  if (
    keysSansComment.length === 2 &&
    keysSansComment.includes('key') &&
    'key' in properties
  ) {
    keysSansComment = [properties.key as string];
  }
  return keysSansComment.map((key) => key.toLowerCase());
}

export async function extractEvaluatorFeedbackKeys(
  organizationId: string,
  value: StructuredEvaluatorSchema
) {
  if ('hub_ref' in value && value.hub_ref) {
    const [owner, promptName, commitHash] = parseOwnerRepoCommit(value.hub_ref);
    const commit = await fetcher<CommitManifestResponse, { is_view: boolean }>({
      url: `${hubApiCommitsPath}/${owner}/${promptName}/${
        commitHash ?? 'latest'
      }`,
      headers: { 'X-Tenant-Id': organizationId },
      params: { is_view: true },
    });

    const manifest = StructuredPromptManifestSchema.parse(commit.manifest);
    return getFeedbackKeySansComment(
      Object.keys(manifest.kwargs.schema_.properties),
      manifest.kwargs.schema_.properties
    );
  }

  if ('schema' in value && value.schema) {
    const schema = StructuredPromptJsonSchema.parse(value.schema);
    return getFeedbackKeySansComment(
      Object.keys(schema.properties),
      schema.properties
    );
  }

  return [];
}

/**
 * Extracts feedback keys from code evaluator functions using Lezer parsers.
 *
 * This function uses AST parsing to robustly extract feedback keys from evaluator code.
 * It handles both Python and JavaScript code with proper syntax tree analysis.
 *
 * @param code - The evaluator code string
 * @param evaluatorName - Fallback name if no keys can be extracted
 * @param language - The programming language ('python' | 'javascript')
 * @returns Array of feedback key strings
 */
export function extractCodeEvaluatorFeedbackKeys(
  code: string,
  evaluatorName?: string,
  language: 'python' | 'javascript' = 'python'
): string[] {
  try {
    // Parse the code using appropriate Lezer parser
    const parser = language === 'javascript' ? javascriptParser : pythonParser;
    const tree = parser.parse(code);

    // Find the evaluator function
    const functionName =
      language === 'javascript' ? 'performEval' : 'perform_eval';
    const evaluatorFunction = findEvaluatorFunction(tree, code, functionName);

    if (evaluatorFunction) {
      // Extract return statements from the function
      const returnStatements = findReturnStatements(evaluatorFunction);

      // Extract keys from return values
      for (const returnStmt of returnStatements) {
        const keys = extractKeysFromReturnValue(returnStmt, code, language);
        if (keys.length > 0) {
          return keys;
        }
      }

      // If no keys found from return statements, search the entire function for dictionary literals
      // This handles cases like dictionary comprehensions where keys come from other dictionaries in the function
      const functionKeys = extractKeysFromEntireFunction(
        evaluatorFunction,
        code,
        language
      );
      if (functionKeys.length > 0) {
        return functionKeys;
      }
    }

    return evaluatorName ? [evaluatorName] : [];
  } catch (error) {
    return evaluatorName ? [evaluatorName] : [];
  }
}

/**
 * Finds the evaluator function in the AST.
 * For JavaScript: looks for 'performEval' function
 * For Python: looks for 'perform_eval' function
 */
function findEvaluatorFunction(
  tree: SyntaxNode,
  code: string,
  functionName: string
): SyntaxNode | null {
  let foundFunction: SyntaxNode | null = null;
  let iterationCount = 0;
  const maxIterations = 1000; // Prevent infinite loops

  const cursor = tree.cursor();

  // Traverse the AST to find the function
  const traverse = (cursor: TreeCursor): boolean => {
    iterationCount++;
    if (iterationCount > maxIterations) {
      return true; // Stop traversal
    }

    do {
      const nodeName = cursor.name;

      // Check for function declarations and definitions
      if (
        nodeName === 'FunctionDeclaration' ||
        nodeName === 'FunctionDefinition' ||
        nodeName === 'VariableDeclaration'
      ) {
        // Check if this contains our target function name
        const headerText = code.slice(cursor.from, cursor.to);
        if (headerText.includes(functionName)) {
          foundFunction = cursor.node;
          return true;
        }
      }

      if (cursor.firstChild()) {
        if (traverse(cursor)) return true;
        cursor.parent();
      }
    } while (cursor.nextSibling());

    return false;
  };

  traverse(cursor);
  return foundFunction;
}

/**
 * Finds all return statements within a function node.
 */
function findReturnStatements(functionNode: SyntaxNode): SyntaxNode[] {
  const returnStatements: SyntaxNode[] = [];
  let iterationCount = 0;
  const maxIterations = 1000;

  const cursor = functionNode.cursor();

  const traverse = (cursor: TreeCursor): void => {
    iterationCount++;
    if (iterationCount > maxIterations) {
      return;
    }

    do {
      if (cursor.name === 'ReturnStatement') {
        returnStatements.push(cursor.node);
      }

      if (cursor.firstChild()) {
        traverse(cursor);
        cursor.parent();
      }
    } while (cursor.nextSibling());
  };

  traverse(cursor);
  return returnStatements;
}

/**
 * Extracts keys from a return statement value (dictionary/object).
 */
function extractKeysFromReturnValue(
  returnNode: SyntaxNode,
  code: string,
  language: 'python' | 'javascript'
): string[] {
  const keys: string[] = [];
  let iterationCount = 0;
  const maxIterations = 500;

  const cursor = returnNode.cursor();

  // Look for dictionary/object literals
  const traverse = (cursor: TreeCursor): void => {
    iterationCount++;
    if (iterationCount > maxIterations) {
      return;
    }

    do {
      const nodeName = cursor.name;

      // JavaScript object literals or Python dictionary literals
      if (
        nodeName === 'ObjectExpression' ||
        nodeName === 'DictionaryExpression' ||
        nodeName === 'SetComprehension' ||
        nodeName === 'DictDisplay'
      ) {
        extractKeysFromObjectNode(cursor, code, keys, language);
      }

      // Python dict() constructor calls
      if (language === 'python' && nodeName === 'CallExpression') {
        const callText = code.slice(cursor.from, cursor.from + 10);
        if (callText.includes('dict(')) {
          extractKeysFromDictCall(cursor, code, keys);
        }
      }

      if (cursor.firstChild()) {
        traverse(cursor);
        cursor.parent();
      }
    } while (cursor.nextSibling());
  };

  traverse(cursor);
  return keys;
}

/**
 * Extracts keys from an object/dictionary node.
 */
function extractKeysFromObjectNode(
  cursor: TreeCursor,
  code: string,
  keys: string[],
  language: 'python' | 'javascript'
): void {
  const objectCursor = cursor.node.cursor();
  let iterationCount = 0;
  const maxIterations = 500;

  const traverse = (cursor: TreeCursor): void => {
    iterationCount++;
    if (iterationCount > maxIterations) {
      return;
    }

    do {
      const nodeName = cursor.name;
      const nodeText = code.slice(cursor.from, cursor.to);

      // Handle JavaScript object properties
      if (language === 'javascript') {
        if (nodeName === 'Property') {
          // Try to find the key in the property
          const propCursor = cursor.node.cursor();
          if (propCursor.firstChild()) {
            const keyText = code.slice(propCursor.from, propCursor.to);

            let cleanKey = keyText;
            // Remove quotes if present
            if (
              (keyText.startsWith('"') && keyText.endsWith('"')) ||
              (keyText.startsWith("'") && keyText.endsWith("'"))
            ) {
              cleanKey = keyText.slice(1, -1);
            }

            if (cleanKey && !keys.includes(cleanKey)) {
              keys.push(cleanKey);
            }
          }
        }
      } else if (language === 'python') {
        // Handle Python dictionary pairs
        if (
          nodeName === 'DictEntry' ||
          nodeName === 'DictionaryEntry' ||
          nodeName === 'Pair'
        ) {
          // Try to find the key in the pair
          const pairCursor = cursor.node.cursor();
          if (pairCursor.firstChild()) {
            const keyText = code.slice(pairCursor.from, pairCursor.to);

            let cleanKey = keyText;
            // Remove quotes if present
            if (
              (keyText.startsWith('"') && keyText.endsWith('"')) ||
              (keyText.startsWith("'") && keyText.endsWith("'"))
            ) {
              cleanKey = keyText.slice(1, -1);
            }

            if (cleanKey && !keys.includes(cleanKey)) {
              keys.push(cleanKey);
            }
          }
        }
      }

      // Also check for direct string/identifier nodes that could be keys
      if (nodeName === 'String' || nodeName === 'Identifier') {
        // Check if this is likely a key (followed by colon)
        const nextChar = code[cursor.to];
        if (nextChar === ':') {
          let cleanKey = nodeText;
          // Remove quotes if present
          if (
            (nodeText.startsWith('"') && nodeText.endsWith('"')) ||
            (nodeText.startsWith("'") && nodeText.endsWith("'"))
          ) {
            cleanKey = nodeText.slice(1, -1);
          }

          if (cleanKey && !keys.includes(cleanKey)) {
            keys.push(cleanKey);
          }
        }
      }

      if (cursor.firstChild()) {
        traverse(cursor);
        cursor.parent();
      }
    } while (cursor.nextSibling());
  };

  traverse(objectCursor);
}

/**
 * Extracts keys from Python dict() constructor calls using regex as fallback.
 * Since the dict() calls have a specific, predictable structure, regex is reliable here.
 */
function extractKeysFromDictCall(
  cursor: TreeCursor,
  code: string,
  keys: string[]
): void {
  // Get the text of the entire call expression
  const callText = code.slice(cursor.node.from, cursor.node.to);

  // Use regex to extract keyword arguments from dict() calls
  // Pattern matches: keyword=value pairs in dict() calls
  const keywordArgPattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\s*=/g;
  let match;

  while ((match = keywordArgPattern.exec(callText)) !== null) {
    const keyName = match[1];
    if (keyName !== 'dict' && !keys.includes(keyName)) {
      keys.push(keyName);
    }
  }
}

/**
 * Extracts keys from dictionary literals found anywhere in the function.
 * This handles cases like dictionary comprehensions where keys come from other dictionaries in the function.
 * For example: return {k: v for k, v in foo.items()} -> find foo = {"key1": val1, "key2": val2}
 */
function extractKeysFromEntireFunction(
  functionNode: SyntaxNode,
  code: string,
  language: 'python' | 'javascript'
): string[] {
  const keys: string[] = [];
  let iterationCount = 0;
  const maxIterations = 1000;

  const cursor = functionNode.cursor();

  const traverse = (cursor: TreeCursor): void => {
    iterationCount++;
    if (iterationCount > maxIterations) {
      return;
    }

    do {
      const nodeName = cursor.name;

      // Look for dictionary literals in variable assignments or elsewhere in the function
      if (language === 'python' && nodeName === 'DictionaryExpression') {
        extractKeysFromObjectNode(cursor, code, keys, language);
      }

      // Also handle JavaScript object literals
      if (language === 'javascript' && nodeName === 'ObjectExpression') {
        extractKeysFromObjectNode(cursor, code, keys, language);
      }

      if (cursor.firstChild()) {
        traverse(cursor);
        cursor.parent();
      }
    } while (cursor.nextSibling());
  };

  traverse(cursor);
  return keys;
}

export function maybeFlattenDict(obj, evaluatorVersion: number) {
  if (
    obj !== null &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 1 &&
    evaluatorVersion < 2
  ) {
    return Object.values(obj)[0];
  }
  return obj;
}

export function getSchemaSuggestions(
  runs: RunSchema[] | ExampleSchema[],
  field: 'inputs' | 'outputs',
  evaluatorVersion: number,
  recursionLevel = 1,
  withoutFlatten = false
): string[] {
  const suggestions = new Set<string>();

  function extractKeys(
    obj: Record<string, any>,
    currentLevel: number,
    prefix = ''
  ) {
    if (currentLevel > recursionLevel) return;

    for (const key in obj) {
      const fullKey = `${prefix}${key}`;
      suggestions.add(fullKey);

      if (
        typeof obj[key] === 'object' &&
        !Array.isArray(obj[key]) &&
        obj[key] !== null
      ) {
        extractKeys(obj[key], currentLevel + 1, `${fullKey}.`);
      }
    }
  }

  runs.forEach((run) => {
    const fieldData = run[field];
    if (withoutFlatten) {
      extractKeys(fieldData, 1);
    } else {
      const flat = maybeFlattenDict(fieldData, evaluatorVersion);
      if (
        fieldData &&
        typeof flat === 'object' &&
        !Array.isArray(flat) &&
        flat !== null
      ) {
        extractKeys(maybeFlattenDict(fieldData, evaluatorVersion), 1);
      }
    }
  });

  return Array.from(suggestions);
}

export const getSuggestions = (
  variant: string,
  templateFormat: string,
  recentRuns: any,
  examples: any,
  ignoreTemplateFormat: boolean,
  evaluatorVersion: number
) => {
  const showNested = templateFormat === 'mustache' || ignoreTemplateFormat;
  const inputSchemaSuggestions =
    recentRuns?.data?.runs &&
    (evaluatorVersion < 2 || variant === 'session' || !examples?.data)
      ? getSchemaSuggestions(
          recentRuns?.data?.runs,
          'inputs',
          evaluatorVersion,
          2,
          true
        )
      : examples.data && variant === 'dataset' && evaluatorVersion >= 2
      ? getSchemaSuggestions(
          examples?.data as ExampleSchema[],
          'inputs',
          evaluatorVersion,
          2,
          true
        )
      : [];
  const outputSchemaSuggestions = recentRuns?.data?.runs
    ? getSchemaSuggestions(
        recentRuns?.data?.runs,
        'outputs',
        evaluatorVersion,
        2,
        true
      )
    : [];
  const inputsAndOutputs = [
    'input',
    ...(showNested ? inputSchemaSuggestions.map((i) => `input.${i}`) : []),
    'output',
    ...(showNested ? outputSchemaSuggestions.map((i) => `output.${i}`) : []),
  ];

  if (variant === 'session') {
    return inputsAndOutputs;
  } else {
    if (!examples?.data) return [...inputsAndOutputs, 'reference_output'];

    const referenceOutputSchemaSuggestions = examples
      ? getSchemaSuggestions(
          examples?.data as ExampleSchema[],
          'outputs',
          evaluatorVersion,
          2
        )
      : [];
    return [
      ...inputsAndOutputs,
      'reference_output',
      ...(showNested
        ? referenceOutputSchemaSuggestions.map((i) => `reference_output.${i}`)
        : []),
    ];
  }
};

function splitOnFirstPeriod(accessor) {
  const index = accessor.indexOf('.');
  if (index === -1) {
    return [accessor];
  }
  return [accessor.substring(0, index), accessor.substring(index + 1)];
}

function getNestedValue(obj, path) {
  if (!path || !obj) return obj;
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (value == null) {
      return undefined;
    }
    value = value[key];
  }
  return value;
}

export function generatePromptInputsFromMapping(
  variableMapping: Record<string, string>,
  input?: Record<string, any>,
  output?: Record<string, any>,
  reference?: Record<string, any>
) {
  const promptInputs = {};

  for (const key in variableMapping) {
    const accessor = variableMapping[key];
    const runParts = splitOnFirstPeriod(accessor);
    let value;

    switch (runParts[0]) {
      case 'input':
        value = getNestedValue(input, runParts[1]);
        break;
      case 'output':
        value = getNestedValue(output, runParts[1]);
        break;
      case 'reference':
        value = getNestedValue(reference, runParts[1]);
        break;
      case 'reference_output':
        value = getNestedValue(reference, runParts[1]);
        break;
      default:
        value = null;
        break;
    }

    promptInputs[key] = value;
  }

  return promptInputs;
}

export function updateVariableMapping(
  inputVars: string[],
  mapping: Record<string, string>
) {
  const updatedMapping = {};

  for (const input of inputVars) {
    if (input in mapping) {
      updatedMapping[input] = mapping[input];
    } else {
      updatedMapping[input] = '';
    }
  }

  return updatedMapping;
}

const TAG_LLM_GENERATIONS_MANIFEST: SerializedConstructor = {
  id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
  lc: 1,
  type: 'constructor',
  kwargs: {
    schema_: {
      type: 'object',
      title: 'Grade',
      required: ['score'],
      properties: {
        score: {
          type: 'integer',
          description: 'Score for the text:',
        },
      },
      description: 'Grade the text based upon the above criteria.',
    },
    messages: [
      {
        id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template:
                'You are a classifier that will score a text include based upon supplied criteria. \n\nHere is the criteria for scoring:\n(1) insults\n(2) threats\n(3) highly negative comments\n(4) any Personally Identifiable Information such as API Keys\n\nScore:\nA score of 1 means that the text meets ANY of the criteria. \nA score of 0 means that the text does not meet ALL of the criteria. \n\nExplain your reasoning in a step-by-step manner to ensure your reasoning and conclusion are correct. ',
              input_variables: [],
              template_format: 'f-string',
            },
          },
        },
      },
      {
        id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template: 'Here is the text: {text}',
              input_variables: ['text'],
              template_format: 'f-string',
            },
          },
        },
      },
    ],
    input_variables: ['text'],
  },
};

const HALLUCINATION_MANIFEST: SerializedConstructor = {
  id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
  lc: 1,
  type: 'constructor',
  kwargs: {
    schema_: {
      type: 'object',
      title: 'Grade',
      required: ['hallucination'],
      properties: {
        hallucination: {
          type: 'integer',
          description: 'Provide the score:',
        },
      },
      description: 'Grade the quiz based upon the above criteria with a score.',
    },
    messages: [
      {
        id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template:
                'You are a teacher grading a quiz. \n\nYou will be given FACTS  and a STUDENT ANSWER. \n\nHere is the grade criteria to follow:\n(1) Ensure the STUDENT ANSWER is grounded in the FACTS. \n(2) Ensure the STUDENT ANSWER does not contain "hallucinated" information outside the scope of the FACTS.\n\nScore:\nA score of 1 means that the student\'s answer meets all of the criteria. This is the highest (best) score. \nA score of 0 means that the student\'s answer does not meet all of the criteria. This is the lowest possible score you can give.\n\nExplain your reasoning in a step-by-step manner to ensure your reasoning and conclusion are correct. \n\nAvoid simply stating the correct answer at the outset.',
              input_variables: [],
              template_format: 'mustache',
            },
          },
        },
      },
      {
        id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template: 'FACTS: {{facts}} \nSTUDENT ANSWER: {{answer}}',
              input_variables: ['facts', 'answer'],
              template_format: 'mustache',
            },
          },
        },
      },
    ],
    input_variables: ['facts', 'answer'],
  },
};

const DOCUMENT_RELEVANCE_RECALL_MANIFEST: SerializedConstructor = {
  id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
  lc: 1,
  type: 'constructor',
  kwargs: {
    schema_: {
      type: 'object',
      title: 'Grade',
      required: ['recall'],
      properties: {
        recall: {
          type: 'integer',
          description: 'Provide the score:',
        },
      },
      description: 'Grade the quiz based upon the above criteria with a score.',
    },
    messages: [
      {
        id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template:
                'You are a teacher grading a quiz.\n\nYou will be given:\n1/ a QUESTION\n2/ a set of FACTS provided by the student\nYou are grading RELEVANCE RECALL.\n\nA score of 1 means that ANY of the FACTS are relevant to the QUESTION.\nA score of 0 means that NONE of the FACTS are relevant to the QUESTION.\n1 is the highest (best) score. 0 is the lowest score you can give.\n\nExplain your reasoning in a step-by-step manner. Ensure your reasoning and conclusion are correct. Avoid simply stating the correct answer at the outset.',
              input_variables: [],
              template_format: 'mustache',
            },
          },
        },
      },
      {
        id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template: 'FACTS: {{facts}} \nQUESTION: {{question}}',
              input_variables: ['facts', 'question'],
              template_format: 'mustache',
            },
          },
        },
      },
    ],
    input_variables: ['facts', 'question'],
  },
};

const DOCUMENT_RELEVANCE_PRECISION_MANIFEST: SerializedConstructor = {
  id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
  lc: 1,
  type: 'constructor',
  kwargs: {
    schema_: {
      type: 'object',
      title: 'Grade',
      required: ['precision'],
      properties: {
        precision: {
          type: 'integer',
          description: 'Provide the score:',
        },
      },
      description: 'Grade the quiz based upon the above criteria with a score.',
    },
    messages: [
      {
        id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template:
                'You are a teacher grading a quiz.\n\nYou will be given:\n1/ a QUESTION\n2/ a set of FACTS provided by the student\n\nYou are grading RELEVANCE PRECISION.\n\nConsider each of the facts.\nA score of 1 means that ALL of the FACTS are relevant to the QUESTION.\nA score of 0 means that ANY of the FACTS are not relevant to the QUESTION.\n\n1 is the highest (best) score.\n0 is the lowest score you can give.\n\nExplain your reasoning in a step-by-step manner.\nEnsure your reasoning and conclusion are correct.\nAvoid simply stating the correct answer at the outset.',
              input_variables: [],
              template_format: 'mustache',
            },
          },
        },
      },
      {
        id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template: 'FACTS: {{facts}} \nQUESTION: {{question}}',
              input_variables: ['facts', 'question'],
              template_format: 'mustache',
            },
          },
        },
      },
    ],
    input_variables: ['facts', 'question'],
  },
};

const ANSWER_HELPFULNESS_MANIFEST: SerializedConstructor = {
  id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
  lc: 1,
  type: 'constructor',
  kwargs: {
    schema_: {
      type: 'object',
      title: 'Grade',
      required: ['helpful'],
      properties: {
        helpful: {
          type: 'integer',
          description: 'Provide the score:',
        },
      },
      description: 'Grade the quiz based upon the above criteria with a score.',
    },
    messages: [
      {
        id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template:
                "You are a teacher grading a quiz. \n\nYou will be given a QUESTION and a STUDENT ANSWER. \n\nHere is the grade criteria to follow:\n(1) Ensure the STUDENT ANSWER is concise and relevant to the QUESTION\n(2) Ensure the STUDENT ANSWER helps to answer the QUESTION\n\nScore:\nA score of 1 means that the student's answer meets all of the criteria. This is the highest (best) score. \nA score of 0 means that the student's answer does not meet all of the criteria. This is the lowest possible score you can give.\n\nExplain your reasoning in a step-by-step manner to ensure your reasoning and conclusion are correct. \n\nAvoid simply stating the correct answer at the outset.",
              input_variables: [],
              template_format: 'mustache',
            },
          },
        },
      },
      {
        id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template:
                'STUDENT ANSWER: {{student_answer}}\nQUESTION: {{question}}',
              input_variables: ['student_answer', 'question'],
              template_format: 'mustache',
            },
          },
        },
      },
    ],
    input_variables: ['student_answer', 'question'],
  },
};

export const DEFAULT_SESSION_FEW_SHOT_EVALUATOR_MANIFEST: SerializedConstructor =
  {
    id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
    lc: 1,
    type: 'constructor',
    kwargs: {
      schema_: {
        type: 'object',
        title: 'Grade',
        required: ['score'],
        properties: {
          score: {
            type: 'integer',
            description: 'Provide the score:',
          },
        },
        description:
          'Grade the quiz based upon the above criteria with a score.',
      },
      messages: [
        {
          id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
          lc: 1,
          type: 'constructor',
          kwargs: {
            prompt: {
              id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
              lc: 1,
              type: 'constructor',
              kwargs: {
                template:
                  "You are assessing a chat bot RESPONSE to a user's QUERY based on a set of criteria. A score of 1 is best and means that the response fits the criteria, whereas a score of 0 is worst and means that it did not fit the criteria.\n\nCriteria:\n<Your criteria here>\n\nUse the examples below for reference.\n{{Few-shot examples}}",
                input_variables: [],
                template_format: 'mustache',
              },
            },
          },
        },
        {
          id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
          lc: 1,
          type: 'constructor',
          kwargs: {
            prompt: {
              id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
              lc: 1,
              type: 'constructor',
              kwargs: {
                template: 'QUERY: {{query}}\nRESPONSE: {{response}}',
                input_variables: ['query', 'response'],
                template_format: 'mustache',
              },
            },
          },
        },
      ],
      input_variables: ['query', 'response'],
    },
  };

export const DEFAULT_DATASET_FEW_SHOT_EVALUATOR_MANIFEST: SerializedConstructor =
  {
    id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
    lc: 1,
    type: 'constructor',
    kwargs: {
      schema_: {
        type: 'object',
        title: 'Grade',
        required: ['score'],
        properties: {
          score: {
            type: 'integer',
            description: 'Provide the score:',
          },
        },
        description:
          'Grade the quiz based upon the above criteria with a score.',
      },
      messages: [
        {
          id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
          lc: 1,
          type: 'constructor',
          kwargs: {
            prompt: {
              id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
              lc: 1,
              type: 'constructor',
              kwargs: {
                template:
                  "You are assessing a chat bot RESPONSE to a user's QUERY in comparison to a REFERENCE response. A score of 1 is best and means that the response fits the criteria, whereas a score of 0 is worst and means that it did not fit the criteria.\n\nCriteria:\n<Your criteria here>\n\nUse the examples below for reference.\n{{Few-shot examples}}",
                input_variables: [],
                template_format: 'mustache',
              },
            },
          },
        },
        {
          id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
          lc: 1,
          type: 'constructor',
          kwargs: {
            prompt: {
              id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
              lc: 1,
              type: 'constructor',
              kwargs: {
                template:
                  'QUERY: {{query}}\nRESPONSE: {{response}}\nREFERENCE: {{reference}}',
                input_variables: ['query', 'response'],
                template_format: 'mustache',
              },
            },
          },
        },
      ],
      input_variables: ['query', 'response', 'reference'],
    },
  };

export interface CustomEvaluator {
  name: string;
  description: string;
  variables: string[];
  manifest: SerializedConstructor;
}

export const SUGGESTED_EVALUATORS: CustomEvaluator[] = [
  {
    name: 'hallucination',
    description:
      'Evaluate whether a RAG answer is supported by the retrieved documents. This is useful for catching cases of answer hallucination.',
    variables: ['facts', 'answer'],
    manifest: HALLUCINATION_MANIFEST,
  },
  {
    name: 'document relevance recall',
    description:
      'Evaluate whether ANY of the retrieved documents for RAG are relevant to the user input question. This is useful for catching cases of bad retrieval.',
    variables: ['facts', 'question'],
    manifest: DOCUMENT_RELEVANCE_RECALL_MANIFEST,
  },
  {
    name: 'document relevance precision',
    description:
      'Evaluate whether ALL retrieved documents for RAG are relevant to the user input question. This is useful for catching cases of imprecise retrieval.',
    variables: ['facts', 'question'],
    manifest: DOCUMENT_RELEVANCE_PRECISION_MANIFEST,
  },
  {
    name: 'answer helpfulness',
    description:
      'Evaluate whether a RAG answer is helpful to address the question. This is useful because it does not require a ground truth answer.',
    variables: ['student_answer', 'question'],
    manifest: ANSWER_HELPFULNESS_MANIFEST,
  },
  {
    name: 'tagging',
    description:
      'Tagging input based upon specific criteria (e.g. for toxicity, etc.)',
    variables: ['text'],
    manifest: TAG_LLM_GENERATIONS_MANIFEST,
  },
];

export function interpolateFewShotPrompt(
  prompt: MessageTuple[],
  fewShotPrompt?: string | null,
  variableMapping?: Record<string, string> | null
) {
  const promptToUse = [...prompt];
  if (fewShotPrompt && variableMapping) {
    for (let i = 0; i < promptToUse.length; i++) {
      const message = promptToUse[i];
      let messageContent = message[1];

      const wrapWithExamples = (content: string) =>
        `{{#examples_few_shot}}\n${content}\n{{/examples_few_shot}}`;

      const finalTemplate = wrapWithExamples(fewShotPrompt);

      messageContent = messageContent.replace(
        '{{few_shot_examples}}',
        finalTemplate
      );

      // update prompt
      promptToUse[i] = [message[0], messageContent];
    }
  }
  return promptToUse;
}
